import type { CompanyExecutive } from "../types/company";
import { firstGivenName } from "./person-name";

/**
 * Who among a company's registry executives is one of its directors, and who
 * runs it — shared by the summary card, which introduces them, and the key
 * contacts search, which looks them up among the company's people.
 *
 * This file is consumed by `@shaarp/ui`. `@shaarp/api` cannot import a runtime
 * value from this package (its barrel re-exports a directory, which Node
 * refuses to resolve from the API's CommonJS build), so it keeps a mirrored
 * copy in `api/src/common/utils/company-executives.ts`, held to this one by
 * `api/src/common/utils/company-executives-parity.spec.ts`. Keep both in sync.
 */

/**
 * Registry roles that are no direction of the company: its auditors, and the
 * people a court appoints to wind it up or administer it.
 */
const NON_DIRECTOR_ROLES = new Set([
  "commissaire aux comptes titulaire",
  "commissaire aux comptes suppléant",
  "commissaire aux comptes",
  "liquidateur",
  "liquidateur amiable",
  "liquidateur judiciaire",
  "administrateur judiciaire",
  "mandataire judiciaire",
]);

/** Registry roles of the person who runs the company. */
const HEAD_ROLES = new Set([
  "président directeur général",
  "président-directeur général",
  "directeur général",
  "président",
  "gérant",
  "chef d'entreprise",
  "chief executive officer",
  "ceo",
]);

/**
 * The roles of an executive, lowercased: the registry joins several with a
 * comma ("Président, Administrateur").
 */
function executiveRoles(executive: CompanyExecutive): string[] {
  return (executive.role ?? "")
    .split(",")
    .map((role) => role.trim().toLowerCase())
    .filter(Boolean);
}

/**
 * The executives to introduce: no legal entity (a holding, an auditing firm)
 * and nobody whose every role is outside the direction.
 */
export function filterDirectors(
  executives: readonly CompanyExecutive[],
): CompanyExecutive[] {
  return executives.filter((executive) => {
    if (executive.isLegalEntity) return false;
    const roles = executiveRoles(executive);
    return (
      roles.length === 0 || roles.some((role) => !NON_DIRECTOR_ROLES.has(role))
    );
  });
}

/** Whether a director runs the company: président, gérant, directeur général… */
export function isCompanyHead(executive: CompanyExecutive): boolean {
  return executiveRoles(executive).some((role) => HEAD_ROLES.has(role));
}

/** The director who runs the company, else the first one; none for an empty list. */
export function findCompanyHead(
  directors: readonly CompanyExecutive[],
): CompanyExecutive | null {
  return directors.find(isCompanyHead) ?? directors[0] ?? null;
}

/**
 * The name a director goes by: the first of their given names — the registry
 * may list them all ("Philippe Marie Raymond") or comma-join them — and their
 * last name without the one in parentheses ("Peyre (Borelly)").
 */
export function directorUsualName(executive: CompanyExecutive): {
  firstName: string;
  lastName: string;
} {
  const firstName = firstGivenName(executive.firstName);
  const lastName = executive.lastName
    .replace(/\([^)]*\)/g, " ")
    .replace(/\s+/g, " ")
    .trim();
  return { firstName, lastName };
}
